// Per spec/blocks.md §1. Branded chapter header — always the first block in
// every template.
//
// **P-037 redesign.** The header is a per-style PNG with everything
// composited in (navy strip + YPO logo + "Southern 7 Chapter" + triangle
// decoration + version color + version text). Render is a single `<img>`
// tag per style — no overlay, no VML, no Outlook-specific markup. Email-
// client-safe by construction (every client renders inline `<img>` tags
// identically).
//
// **Schema change.** `style` enum widened from 3 values
// (newsletter / events / general) to 7 (signal / state-event / big-3 /
// signature-event / announcement / prospects / new-members) — Update was
// dropped from the plan. `subhead` input dropped entirely; text is baked
// into each PNG. See D-### for the migration shim that maps old style
// values at the persistence layer.
//
// **Asset URLs are absolutized at render time (P-037.26 D-134).** The
// per-style PNG paths declared in `STYLE_THEME` below are relative
// (`/ypo-s7-email-header-*.png`), served from `public/`. The hand-
// rolled `<img>` below passes `theme.imageUrl` through
// `toAbsoluteUrl` so the rendered HTML carries the absolute origin
// (defaulting to today's Firebase Hosting URL). Required for View-in-
// Tab + Constant Contact send paths where the email's document base
// can't resolve repo-relative paths. See src/render/constants.ts.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html, toAbsoluteUrl } from '../render'
import { tokens } from '../tokens'

const optionsSchema = z.object({
  style: z.enum([
    'signal',
    'state-event',
    'big-3',
    'signature-event',
    'announcement',
    'prospects',
    'new-members',
  ]),
})

const inputsSchema = z.object({})

type HeaderStyle = z.infer<typeof optionsSchema>['style']

interface HeaderTheme {
  /** Per-style PNG path served from `public/`. */
  readonly imageUrl: string
  /** Alt text for screen readers / image-blocking clients. */
  readonly alt: string
}

// Filenames match what Banks dropped in `public/` (mixed-case + per-style
// suffixes per the source-PSD naming). Hardcoded mapping (rather than a
// `${style}.png` derivation) so the test surface fails loudly if a file
// is renamed/missing rather than silently 404'ing in production.
// P-037.18 D-118: per-style link color drives `RenderContext.linkColor` —
// renderEmail looks up `STYLE_LINK_COLOR[firstHeader.options.style]` and
// threads it through every block's RenderContext. Per Banks's mapping —
// Announcement stays orange (#ea7600); Prospects + new-members rebranded
// to red (#DA291C) post-2026-05-27 chapter graphic refresh, alongside the
// matching header + subheader PNG swaps in `public/`. When no Header
// block is in the Email (or all Header blocks are `included: false`),
// renderEmail falls back to `tokens.color['link-default']`.
export const STYLE_LINK_COLOR: Record<HeaderStyle, string> = {
  signal: '#d69d23',
  'state-event': '#0090da',
  'big-3': '#64a70b',
  'signature-event': '#289989',
  announcement: '#ea7600',
  prospects: '#DA291C',
  'new-members': '#DA291C',
}

// P-037.23 D-131: per-style footer-background tint. Mirrors the D-118
// linkColor pattern + the D-125 subheaderImageUrl pattern — renderEmail
// picks the first included Header, looks up `STYLE_TINT_10[style]`, and
// threads it via `RenderContext.tintColor`. Footer's outer-table bgcolor
// reads `ctx.tintColor`; consumers added in P-037.24 (the new
// big3-signature-events Block) will reuse the same context field. Banks-
// confirmed values: each entry is the 10% blend of the per-style link
// color over white (so the per-Email family is visually coherent — the
// footer's quiet bg + any future tint-bg surface picks the same accent
// hue as the Email's gold/blue/green/teal/orange link color, washed out
// to ~10%). Pre-computed hex literals because `opacity` / `rgba()` are
// unreliable in email (Outlook desktop's Word rendering engine drops
// both silently). Default (no Header / link-default) falls back to the
// 10% blend of `tokens.color['link-default']` over white.
export const STYLE_TINT_10: Record<HeaderStyle, string> = {
  signal: '#fbf5e9',
  'state-event': '#e6f4fb',
  'big-3': '#f0f6e7',
  'signature-event': '#eaf5f3',
  announcement: '#fdf1e6',
  prospects: '#fbeae8',
  'new-members': '#fbeae8',
}

/** Default tint when no Header block is present — 10% blend of
 *  `tokens.color['link-default']` (#c69b34) over white. Co-located here
 *  so the entire tint surface lives in one file. */
export const TINT_DEFAULT = '#f9f5eb'

// P-037.19 D-125: per-style subheader banner. Mirrors the D-118 linkColor
// pattern — renderEmail picks the first included Header, looks up
// `STYLE_SUBHEADER[style]`, and threads it via `RenderContext.subheaderImageUrl`.
// Only the `section-heading` Block consumes it today; other SectionHeader
// callers (day-chairs, chapter-sponsors, …) leave the prop unset and render
// without a banner. Filenames match `public/ypo-s7-email-subheader-*.png`,
// using the same mixed-case suffixes as `STYLE_THEME` so the two PNG sets
// share a naming family. Banner is purely decorative (300 × 35 chevron bar
// color-coded per email type); the Component renders it with `alt=""`.
export const STYLE_SUBHEADER: Record<HeaderStyle, string> = {
  signal: '/ypo-s7-email-subheader-s7-signal.png',
  'state-event': '/ypo-s7-email-subheader-state-event.png',
  'big-3': '/ypo-s7-email-subheader-big-3-meeting.png',
  'signature-event': '/ypo-s7-email-subheader-Signature-Event.png',
  announcement: '/ypo-s7-email-subheader-Announcement.png',
  prospects: '/ypo-s7-email-subheader-Prospects.png',
  'new-members': '/ypo-s7-email-subheader-new-members.png',
}

// P-037.28 D-136: per-style CTA button background. Fourth use of the
// first-included-Header derivation pattern (after D-118 linkColor +
// D-125 subheaderImageUrl + D-131 tintColor). The new `button` Block
// (item 13 revised) reads `ctx.ctaBgColor` for its button bg so freeform
// CTAs visually inherit the per-Email family. Values mirror
// STYLE_LINK_COLOR — the rationale is "the Email's per-style accent
// colors the CTAs," not "CTAs have their own palette."
export const STYLE_CTA_BG: Record<HeaderStyle, string> = {
  signal: '#d69d23',
  'state-event': '#0090da',
  'big-3': '#64a70b',
  'signature-event': '#289989',
  announcement: '#ea7600',
  prospects: '#DA291C',
  'new-members': '#DA291C',
}

/** Default CTA button bg when no Header block is present. Matches the
 *  pre-D-136 text-block button bg (`#162443` navy) — keeps the no-Header
 *  fallback visually identical to the chapter's prior CTA color. */
export const CTA_BG_DEFAULT = '#162443'

const STYLE_THEME: Record<HeaderStyle, HeaderTheme> = {
  signal: {
    imageUrl: '/ypo-s7-email-header-s7-signal.png',
    alt: 'YPO Southern 7 Chapter — The S7 Signal',
  },
  'state-event': {
    imageUrl: '/ypo-s7-email-header-state-event.png',
    alt: 'YPO Southern 7 Chapter — State Event',
  },
  'big-3': {
    imageUrl: '/ypo-s7-email-header-big-3-meeting.png',
    alt: 'YPO Southern 7 Chapter — Big 3 Event',
  },
  'signature-event': {
    imageUrl: '/ypo-s7-email-header-Signature-Event.png',
    alt: 'YPO Southern 7 Chapter — Signature Event',
  },
  announcement: {
    imageUrl: '/ypo-s7-email-header-Announcement.png',
    alt: 'YPO Southern 7 Chapter — Announcement',
  },
  prospects: {
    imageUrl: '/ypo-s7-email-header-Prospects.png',
    alt: 'YPO Southern 7 Chapter — Prospects',
  },
  'new-members': {
    imageUrl: '/ypo-s7-email-header-new-members.png',
    alt: 'YPO Southern 7 Chapter — New Members',
  },
}

const HEADER_WIDTH = tokens.layout.containerWidth // 680
const HEADER_HEIGHT = 130

export const header = defineBlock({
  id: 'header',
  label: 'Header',
  category: 'header',
  optionsSchema,
  inputsSchema,
  defaultOptions: { style: 'signal' },
  defaultInputs: {},
  render: ({ options }) => {
    const theme = STYLE_THEME[options.style]
    const absoluteImageUrl = toAbsoluteUrl(theme.imageUrl)
    // P-037.18 D-118: the `<td>` carries bulletproof "image-flush" styling —
    // `padding: 0; line-height: 0; font-size: 0;` and `valign="top"` —
    // so no implicit whitespace renders above the image in any email
    // client. Banks's audit: "remove padding above image. Image should
    // be flush with the top." (The IMG already had `display: block`; the
    // bulletproof attrs close the gap in clients that add UA-default
    // line-height around cells regardless of inline IMG styling.)
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${HEADER_WIDTH}" style="border-collapse: collapse;"><tr><td valign="top" style="padding: 0; line-height: 0; font-size: 0;"><img src="${absoluteImageUrl}" alt="${theme.alt}" width="${HEADER_WIDTH}" height="${HEADER_HEIGHT}" style="display: block; width: ${HEADER_WIDTH}px; height: ${HEADER_HEIGHT}px; border: 0; outline: none; text-decoration: none;"></td></tr></table>`
  },
})
